import { useQuery } from "@tanstack/react-query";
import type { SupabaseClient } from "@supabase/supabase-js";
import { useSupabaseClient } from "@/hooks/useSupabaseClient";

export interface InviteMonthStats {
  joined: number;
  optedOut: number;
}

/**
 * This-month join/opt-out counts for the Invitation tab's "This month" panel:
 * campaign_recipients of the given invite batches whose outcome landed. Two
 * head-count queries (subscribed / opted_out), so it stays cheap even when the
 * backlog sweep has queued thousands of recipients. Invited/failed totals come
 * from the batch rows themselves client-side; only the reply outcomes need this.
 */
export const useInviteMonthStats = (campaignIds: string[]) => {
  const getSupabaseClient = useSupabaseClient();

  return useQuery<InviteMonthStats>({
    queryKey: ["sms-invite-month-stats", campaignIds],
    enabled: campaignIds.length > 0,
    queryFn: async () => {
      const supabase = await getSupabaseClient();
      if (!supabase) throw new Error("Not authenticated");
      const sb = supabase as unknown as SupabaseClient;

      const countOutcome = async (outcome: string) => {
        const { count, error } = await sb
          .from("campaign_recipients")
          .select("id", { count: "exact", head: true })
          .in("campaign_id", campaignIds)
          .eq("outcome", outcome);
        if (error) throw error;
        return count ?? 0;
      };

      const [joined, optedOut] = await Promise.all([countOutcome("subscribed"), countOutcome("opted_out")]);
      return { joined, optedOut };
    },
    staleTime: 60 * 1000,
  });
};
